import { ApiCultureSettings } from "ComponentModels/ApiCultureSettings";
import { CultureListItem } from "Components/main-menu/useCultureSelector";

export interface GetSelectableCulturesParams {
	settings: ApiCultureSettings;
	cultureList: CultureListItem[];
	currentCultureCode: string;
}

const insensitiveEquals = (string1: string, string2: string) => {
	return string1?.trim().toLowerCase() === string2?.trim().toLowerCase();
};

const mainCultureSubCultureCodes = (settings: ApiCultureSettings, currentCultureCode: string): string[] =>
	settings?.Cultures?.find((culture) => insensitiveEquals(culture.Main, currentCultureCode))?.SubCultures || [];

const nonMainCultureSubCultureCodes = (settings: ApiCultureSettings, currentCultureCode: string): string[] => {
	const mainCultureSubCultures = mainCultureSubCultureCodes(settings, currentCultureCode);

	return (
		settings?.AllCultures.filter(
			(cultureCode) =>
				!mainCultureSubCultures.some((subCultureCode) => insensitiveEquals(subCultureCode, cultureCode))
		) || []
	);
};

const convertCodesToCultureListItems = (cultureList: CultureListItem[], cultureCodes: string[]): CultureListItem[] =>
	cultureCodes.map((cultureCode) => mapToCultureListItem(cultureList, cultureCode)).filter((listItem) => listItem);

const getSelectableMainCultureSubCultures = (params: GetSelectableCulturesParams): CultureListItem[] =>
	convertCodesToCultureListItems(
		params.cultureList,
		mainCultureSubCultureCodes(params.settings, params.currentCultureCode)
	);

const getSelectableNonMainCultureSubCultures = (params: GetSelectableCulturesParams): CultureListItem[] =>
	convertCodesToCultureListItems(
		params.cultureList,
		nonMainCultureSubCultureCodes(params.settings, params.currentCultureCode)
	).sort((a, b) => (a.name.toLowerCase() < b.name.toLowerCase() ? -1 : 1));

export const mapToCultureListItem = (cultureList: CultureListItem[], cultureCode: string) =>
	cultureList.find((listItem) => insensitiveEquals(listItem.culture, cultureCode));

export const isCultureEnabled = (settings: ApiCultureSettings, cultureCode: string) =>
	!settings?.Cultures.some((culture) => culture.IsDisabled && insensitiveEquals(culture.Main, cultureCode));

export const getSelectableCulturesInOrder = (params: GetSelectableCulturesParams): CultureListItem[] => {
	if (!params.settings) return params.cultureList;

	const mainCultureSubCultures = getSelectableMainCultureSubCultures(params);
	const remainingSubCultures = getSelectableNonMainCultureSubCultures(params);

	return [...mainCultureSubCultures, ...remainingSubCultures];
};
